import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RootState } from '../../../store/redux/store/store';
import { getUserCompanyDepartments } from "@/api/PreformanceManagement/Goals";
import { Award } from 'lucide-react';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useWriteAccess, RESTRICTED_WRITE_REASON_ID } from '../../../hooks/useWriteAccess';
import RestrictedWriteNotice, { RestrictedWriteHint } from '../../../components/subscription/RestrictedWriteNotice';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Spinner } from '../../../components/ui/Spinner';
import Progress from '../../../components/ui/Progress';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import RecognitionCard from "./components/RecognitionCard";
import RecognitionComposer from "./components/RecognitionComposer";
import FilterBar from "./components/FilterBar";
import RecognitionGapsTab, { RecognitionGapDraftPrefill } from "./components/RecognitionGapsTab";
import request from "@/utils/request";
import { RecognitionView, RecognitionRecipientView, CompanyRecognitionValueView } from "@/models/preformanceManagement";
import { Result } from "@/models/general";
import { getFullFeed, getRecognitionUsers, getCompanyRecognitionValues } from "../../../api/PreformanceManagement/Recognition";
import { giveFeedback, getFeedbacksReceived, getFeedbacksGiven, getRecognitionFeedbackConfig } from "../../../api/PreformanceManagement/Feedback";
import { FeedbackType } from "../../../models/preformanceManagement";
import { FeedbackView, FeedbackVisibilityType } from "@/models/preformanceManagement";
import { DepartmentBasicView } from "@/api/PreformanceManagement/Goals";
import { PlusCircle } from "lucide-react";
import { getFullUrl } from "../../../utils/url";

/* ----------------------------------------
   Type Definitions
-----------------------------------------*/
export interface Recognition {
  id: number;
  senderId?: string;
  senderName: string;
  sender: string;
  senderAvatar: string;
  receiverId?: string;
  receiverName: string;
  recipient: string;
  recipientAvatar: string;
  message: string;
  values: string[];
  timestamp: Date;
  points?: number;
  type?: 'recognition' | 'feedback' | 'coaching' | 'concern';
}

export interface Filters {
  values?: string;
  department?: string;
  timeRange?: "today" | "week" | "month" | "quarter" | "all" | string;
}

interface EmployeeOption {
  value: string | number;
  label: string;
  avatar?: string | null;
}

/* ----------------------------------------
   Component
-----------------------------------------*/
const RecognitionFeed: React.FC = () => {
  const { t } = useTranslation();
  // CO-24 — recognition posts are gated writes while the tenant is Restricted/Dormant.
  const write = useWriteAccess();
  const userId = useSelector((state: RootState) => (state as any).user?.userId);
  const imageUrl = useSelector((state: RootState) => (state as any).user?.imageUrl);
  const fullName = useSelector((state: RootState) => (state as any).user?.fullName);
  const userRole = useSelector((state: RootState) => state.user?.role as string);
  const isManager = useSelector((state: RootState) => state.user?.isManager as boolean);
  // Cosmetic gate only — the server is the real one (spec §7.1, §9.1). Roles are exact-cased: "Hr", never "HR".
  // Includes "Manager" role to mirror the server, which grants a Manager their department scope even
  // without direct-report rows; the server still Forbids anyone truly out of scope (tab degrades gracefully).
  const canSeeGaps = ['Hr', 'Admin', 'Chief', 'Manager'].includes(userRole) || !!isManager;
  const { toast, showToast, dismissToast } = useToast();

  const [searchParams] = useSearchParams();

  type TabKey = 'received' | 'given' | 'company' | 'gaps';
  const [activeTab, setActiveTab] = useState<TabKey>('received');

  // Honor ?view=gaps whenever the parameter itself changes, not just on mount: the Q4-d
  // reminder notification navigates client-side, so a recipient already on this page would
  // otherwise get the new URL with the tab never switching. Keyed on the string (not the
  // searchParams object) so it cannot re-fire and trap the user on the gaps tab.
  const viewParam = searchParams.get('view');
  useEffect(() => {
    if (viewParam === 'gaps' && canSeeGaps) setActiveTab('gaps');
  }, [viewParam, canSeeGaps]);

  const [recognitions, setRecognitions] = useState<Recognition[]>([]);
  const [recognitionsRaw, setRecognitionsRaw] = useState<RecognitionView[]>([]);
  const [feedbacksRaw, setFeedbacksRaw] = useState<FeedbackView[]>([]);
  const [filteredRecognitions, setFilteredRecognitions] = useState<Recognition[]>([]);
  const [filters, setFilters] = useState<Filters>({
    values: "all",
    department: "all",
    timeRange: "week",
  });

  const [isComposerOpen, setIsComposerOpen] = useState(false);
  // Set only when the composer is opened pre-filled from a recognition-gap AI draft; null for normal opens.
  const [composerDraft, setComposerDraft] = useState<RecognitionGapDraftPrefill | null>(null);
  // Bumped on every open so the composer remounts and re-applies prefill even when already open.
  const [composerInstance, setComposerInstance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [employeeAvatarMap, setEmployeeAvatarMap] = useState<Record<string, string | null>>({});
  const [departments, setDepartments] = useState<DepartmentBasicView[]>([]);
  const [companyRecognitionValues, setCompanyRecognitionValues] = useState<Array<{ value: string; label: string }>>([]);

  const fetchDepartments = async () => {
    try {
      const depts = await getUserCompanyDepartments();
      setDepartments(depts);
    } catch {
      showToast(t('performance.recognition.failedLoadDepartments'), 'error');
    }
  };

  const fetchCompanyRecognitionValues = async () => {
    try {
      const result = await getCompanyRecognitionValues();
      if (result.payload) {
        const mappedValues = result.payload.map((v: CompanyRecognitionValueView) => ({
          value: v.name,
          label: v.name
        }));
        setCompanyRecognitionValues(mappedValues);
      }
    } catch {
      // Silently fail
    }
  };

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [displayCount, setDisplayCount] = useState(10);
  const ITEMS_PER_PAGE = 10;

  const fetchUsers = async () => {
    try {
      const recipientsData: RecognitionRecipientView[] = await getRecognitionUsers();

      const mappedEmployees: EmployeeOption[] = recipientsData
        .filter((u) => u.recipientId !== userId)
        .map((u) => ({
          value: u.recipientId,
          label: `${u.recipientFirstName} ${u.recipientLastName}`,
          avatar: getFullUrl(u.recipientProfilePictureUrl),
        }));

      const avatarLookup: Record<string, string | null> = {};
      mappedEmployees.forEach((e) => {
        avatarLookup[String(e.value)] = e.avatar ?? null;
      });

      if (userId && imageUrl) {
        avatarLookup[userId] = getFullUrl(imageUrl);
      }

      setEmployees(mappedEmployees);
      setEmployeeAvatarMap(avatarLookup);
    } catch {
      showToast(t('performance.recognition.failedLoadUsers'), 'error');
    }
  };

  const fetchRecognitionsRaw = async (backgroundRefresh = false) => {
    try {
      if (!backgroundRefresh) setIsLoading(true);
      setIsRefreshing(true);

      const response: Result<RecognitionView[]> = await getFullFeed();
      if (response?.payload) {
        // No success toast: a page load is not a user action, and the feed rendering IS the
        // confirmation. Errors still toast.
        setRecognitionsRaw(response.payload);
      } else {
        setRecognitionsRaw([]);
        if (!backgroundRefresh) showToast(t('performance.recognition.noDataAvailable'), 'info');
      }
    } catch {
      setRecognitionsRaw([]);
      if (!backgroundRefresh) showToast(t('performance.recognition.failedLoadFeed'), 'error');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchFeedbacks = async (backgroundRefresh = false) => {
    if (!userId) return;

    try {
      const [receivedResult, givenResult] = await Promise.all([
        getFeedbacksReceived(userId),
        getFeedbacksGiven(userId)
      ]);

      const allFeedbacks: FeedbackView[] = [];

      if (receivedResult?.payload) allFeedbacks.push(...receivedResult.payload);

      if (givenResult?.payload) {
        givenResult.payload.forEach(fb => {
          if (!allFeedbacks.some(existing => existing.id === fb.id)) {
            allFeedbacks.push(fb);
          }
        });
      }

      setFeedbacksRaw(allFeedbacks);
    } catch {
      setFeedbacksRaw([]);
      if (!backgroundRefresh) showToast(t('performance.recognition.failedLoadFeedbacks'), 'error');
    }
  };

  // Map avatars once users and feed are loaded
  useEffect(() => {
    const mapped: Recognition[] = [];

    recognitionsRaw.forEach((item) => {
      const senderAvatar = getFullUrl(item.senderProfilePictureUrl || employeeAvatarMap[item.senderId]) || "";
      const recipientAvatar = getFullUrl(item.recipientProfilePictureUrl || employeeAvatarMap[item.recipientId]) || "";

      mapped.push({
        id: item.id,
        senderId: item.senderId,
        senderName: item.senderName,
        sender: item.senderName,
        senderAvatar,
        receiverId: item.recipientId,
        receiverName: item.recipientName,
        recipient: item.recipientName,
        recipientAvatar,
        message: item.message,
        values: item.values || [],
        timestamp: new Date(item.timestamp + 'Z'),
        points: 0,
        type: 'recognition'
      });
    });

    feedbacksRaw.forEach((item) => {
      const senderAvatar = getFullUrl(employeeAvatarMap[item.givenById]) || "";
      const recipientAvatar = getFullUrl(employeeAvatarMap[item.receivedById]) || "";

      const fbType = item.feedbackType ?? (item as any).FeedbackType;
      let feedbackTypeString: 'feedback' | 'coaching' | 'concern' = 'feedback';

      if (fbType === 'Coaching' || fbType === FeedbackType.Coaching || fbType === 1) {
        feedbackTypeString = 'coaching';
      } else if (fbType === 'Concern' || fbType === FeedbackType.Concern || fbType === 2) {
        feedbackTypeString = 'concern';
      }

      mapped.push({
        id: item.id + 1000000,
        senderId: item.givenById,
        senderName: item.givenByName,
        sender: item.givenByName,
        senderAvatar,
        receiverId: item.receivedById,
        receiverName: item.receivedByName,
        recipient: item.receivedByName,
        recipientAvatar,
        message: item.comment,
        values: [],
        timestamp: new Date(item.createdOn + 'Z'),
        points: 0,
        type: feedbackTypeString
      });
    });

    setRecognitions(mapped);
  }, [recognitionsRaw, feedbacksRaw, employeeAvatarMap]);

  // Auto-refresh setup
  const setupAutoRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);

    if (autoRefreshEnabled) {
      refreshTimerRef.current = setInterval(() => {
        fetchRecognitionsRaw(true);
        fetchFeedbacks(true);
      }, 300000);
    }
  }, [autoRefreshEnabled, userId]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      await fetchUsers();
      await fetchDepartments();
      await fetchCompanyRecognitionValues();
      await fetchRecognitionsRaw();
      await fetchFeedbacks();
    };
    init();
    setupAutoRefresh();

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [setupAutoRefresh]);

  const departmentOptions = departments.length > 0
    ? departments.map(dept => ({ value: dept.id.toString(), label: dept.name }))
    : [];

  // Filtering — first by active tab, then by user filter controls.
  // Tab semantics:
  //  - received: items where the current user is the recipient (recognitions + feedbacks)
  //  - given:    items where the current user is the sender (recognitions + feedbacks)
  //  - company:  all RECOGNITIONS in the company feed (feedbacks excluded — they are 1:1 private)
  useEffect(() => {
    if (activeTab === 'gaps') return;
    let filtered: Recognition[];
    if (activeTab === 'received') {
      filtered = recognitions.filter(r => r.receiverId === userId);
    } else if (activeTab === 'given') {
      filtered = recognitions.filter(r => r.senderId === userId);
    } else {
      filtered = recognitions.filter(r => r.type === 'recognition');
    }

    if (filters.values && filters.values !== "all") {
      filtered = filtered.filter((r) => r.values.includes(filters.values!));
    }

    if (filters.timeRange && filters.timeRange !== "all") {
      const now = new Date().getTime();
      const ranges: Record<string, number> = {
        today: 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000,
        month: 30 * 24 * 60 * 60 * 1000,
        quarter: 90 * 24 * 60 * 60 * 1000,
      };
      const cutoff = now - (ranges[filters.timeRange] || 0);
      filtered = filtered.filter((r) => r.timestamp.getTime() >= cutoff);
    }

    if (filters.department && filters.department !== "all") {
      filtered = filtered.filter((r) => {
        const senderDept = employeeAvatarMap[r.sender] ?? "";
        const recipientDept = employeeAvatarMap[r.recipient] ?? "";
        return senderDept === filters.department || recipientDept === filters.department;
      });
    }

    filtered.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    setFilteredRecognitions(filtered);
    setDisplayCount(ITEMS_PER_PAGE);
  }, [recognitions, filters, employeeAvatarMap, activeTab, userId]);

  // Submit recognition
  const submitRecognition = async (newRecognition: any) => {
    try {
      setIsSubmitting(true);

      const isFeedback = newRecognition.type === 'coaching' || newRecognition.type === 'concern';
      const feedbackTypeLabel = newRecognition.type === 'coaching' ? t('performance.recognition.typeCoaching') :
                               newRecognition.type === 'concern' ? t('performance.recognition.typeConcern') : t('performance.recognition.typeRecognition');

      showToast(`${t('common.submitting')} ${feedbackTypeLabel}...`, 'info');

      let success = false;

      if (isFeedback) {
        const feedbackResult = await giveFeedback({
          receivedById: newRecognition.recipientId,
          comment: newRecognition.message,
          feedbackType: newRecognition.feedbackType
        });

        if (feedbackResult?.payload) {
          success = true;
        } else if (feedbackResult?.errors && feedbackResult.errors.length > 0) {
          showToast(feedbackResult.errors[0].description || t('performance.recognition.submissionFailed'), 'error');
          return false;
        }
      } else {
        const recognitionResponse = await request.post('Recognition/Submit', newRecognition);

        if (recognitionResponse.data?.payload) {
          success = true;
        } else if (recognitionResponse.data?.errors && recognitionResponse.data.errors.length > 0) {
          showToast(recognitionResponse.data.errors[0].description || t('performance.recognition.submissionFailed'), 'error');
          return false;
        }
      }

      if (success) {
        showToast(`${feedbackTypeLabel} ${t('performance.recognition.submittedSuccessfully')}`, 'success');
        setIsRefreshing(true);
        await fetchRecognitionsRaw();
        await fetchFeedbacks();
        return true;
      } else {
        showToast(t('performance.recognition.submissionFailed'), 'error');
        return false;
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.errors?.[0]?.message || t('performance.recognition.errorSubmitting');
      showToast(errorMsg, 'error');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualRefresh = async () => {
    showToast(t('performance.recognition.refreshingFeed'), 'info');
    await fetchRecognitionsRaw();
    await fetchFeedbacks();
  };

  const handleClearFilters = () => {
    setFilters({ values: "all", department: "all", timeRange: "all" });
    setDisplayCount(ITEMS_PER_PAGE);
    showToast(t('performance.recognition.filtersCleared'), 'info');
  };

  const handleLoadMore = () => setDisplayCount(prev => prev + ITEMS_PER_PAGE);
  const handleShowAll = () => setDisplayCount(filteredRecognitions.length);

  const composerRef = useRef<HTMLDivElement | null>(null);

  // The composer mounts ABOVE the gaps list, so generating a draft from a row further down
  // the page changed nothing the user could see — the button finished and the screen sat
  // still, which reads as a dead button. Scroll it into view on every open (composerInstance
  // changes each time, so re-generating for someone else brings you back too). Sighted users
  // get the movement; RecognitionComposer takes focus so everyone else gets the same signal.
  useEffect(() => {
    if (!isComposerOpen) return;
    const el = composerRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [isComposerOpen, composerInstance]);

  // Normal composer open — no AI draft, so the banner never shows.
  const openComposer = () => {
    setComposerDraft(null);
    setComposerInstance(n => n + 1);
    setIsComposerOpen(true);
  };

  // Gap-row drafts no longer open this composer — they fill a compact form under the row
  // itself, so nobody has to scroll to the top of the page and back to find their place.

  const closeComposer = () => {
    setIsComposerOpen(false);
    setComposerDraft(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <PageLayout
        title={t('performance.recognition.title')}
        description={t('performance.recognition.subtitle')}
        icon={Award}
        accentColor="rose"
        maxWidth="1600px"
      >
        <div className="text-center py-16">
          <Spinner size="lg" className="mx-auto" />
          <h2 className="text-lg font-semibold text-foreground mt-4">
            {t('performance.recognition.loadingFeed')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('performance.recognition.fetchingLatest')}
          </p>
        </div>
      </PageLayout>
    );
  }

  // Empty state
  const renderEmptyState = () => (
    <div className="text-center py-12 border-2 border-dashed border-border rounded-xl mt-4 max-w-3xl mx-auto w-full">
      <AppIcon name="Heart" size={60} className="mx-auto text-muted-foreground mb-3" />
      <h2 className="text-xl font-semibold text-foreground mb-2">
        {t('performance.recognition.noRecognitions')}
      </h2>
      <p className="text-base text-muted-foreground mb-4">
        {filters.values !== 'all' || filters.timeRange !== 'all'
          ? t('performance.recognition.noMatchFilters')
          : t('performance.recognition.beFirst')}
      </p>
      {filters.values !== 'all' || filters.timeRange !== 'all' ? (
        <Button variant="outline" onClick={handleClearFilters}>
          {t('performance.recognition.clearFilters')}
        </Button>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Button {...write.buttonProps} onClick={openComposer}>
            <AppIcon name="Heart" size={16} className="me-2" />
            {t('performance.recognition.giveFirstRecognition')}
          </Button>
          {/* CO-24 repair — the empty state can be the only thing on screen; a disabled CTA here
              must carry its reason inline because it cannot render a tooltip. */}
          <RestrictedWriteHint />
        </div>
      )}
    </div>
  );

  return (
    <PageLayout
      title={t('performance.recognition.title')}
      description={t('performance.recognition.subtitle')}
      icon={Award}
      accentColor="rose"
      maxWidth="1600px"
      headerActions={
        <Button variant="outline" onClick={openComposer} {...(write.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} disabled={isSubmitting || write.blocked}>
          <PlusCircle className="me-2" size={16} />
          {t('performance.recognition.giveFeedback')}
        </Button>
      }
    >
      <RestrictedWriteNotice className="mb-4" />

      {/* Refresh Progress Bar */}
      {isRefreshing && (
        <div className="fixed top-0 inset-x-0 z-[9999]">
          <div className="h-1 bg-primary animate-pulse" />
        </div>
      )}

      {/* Tab toggle — Received / Given / Company */}
      <div className="max-w-3xl mx-auto w-full mb-4">
        <div role="tablist" aria-label={t('performance.recognition.tabsLabel')} className="inline-flex bg-muted rounded-lg p-0.5">
          {([
            { id: 'received', label: t('performance.recognition.tabReceived'), icon: 'Heart' },
            { id: 'given', label: t('performance.recognition.tabGiven'), icon: 'Send' },
            { id: 'company', label: t('performance.recognition.tabCompany'), icon: 'Users' },
            ...(canSeeGaps
              ? [{ id: 'gaps' as const, label: t('performance.recognition.tabGaps'), icon: 'AlertTriangle' }]
              : []),
          ] as Array<{ id: TabKey; label: string; icon: string }>).map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`recognition-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${isActive ? 'bg-card shadow-sm text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <AppIcon name={tab.icon as any} size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Composer renders on any tab so an AI draft from the Gaps tab can open it pre-filled. */}
      {isComposerOpen && (
        <div ref={composerRef} className="my-4 max-w-3xl mx-auto w-full scroll-mt-4">
          <RecognitionComposer
            key={composerInstance}
            isOpen={isComposerOpen}
            onClose={closeComposer}
            onSubmit={async (rec) => {
              const success = await submitRecognition(rec);
              if (success) {
                setTimeout(() => fetchRecognitionsRaw(true), 1000);
              }
              return success;
            }}
            employees={employees}
            initialRecipientId={composerDraft?.recipientId ?? null}
            initialMessage={composerDraft?.message ?? ''}
            initialValues={composerDraft?.values ?? []}
            aiDraft={composerDraft?.aiDraft ?? null}
          />
        </div>
      )}

      {activeTab === 'gaps' ? (
        <div className="max-w-3xl mx-auto w-full">
          <RecognitionGapsTab
            departmentId={filters.department !== 'all' ? parseInt(filters.department, 10) : undefined}
            departments={departments}
            onDepartmentChange={(id) =>
              setFilters((f) => ({ ...f, department: id !== undefined ? String(id) : 'all' }))
            }
          />
        </div>
      ) : (
        <>
      {/* Stats Bar */}
          <div className="flex gap-4 mb-4 flex-wrap max-w-3xl mx-auto w-full">
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">{t('performance.recognition.totalRecognitions')}:</span>
              <span className="text-lg font-bold text-foreground">{filteredRecognitions.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">{t('performance.recognition.showing')}:</span>
              <span className="text-lg font-bold text-foreground">
                {Math.min(displayCount, filteredRecognitions.length)} {t('common.of')} {filteredRecognitions.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">{t('performance.recognition.filteredBy')}:</span>
              <span className="text-sm font-medium text-foreground">
                {filters.values !== 'all' && `${t('performance.recognition.value')}: ${filters.values}`}
                {filters.timeRange !== 'all' && ` ${t('performance.recognition.time')}: ${filters.timeRange}`}
                {filters.values === 'all' && filters.timeRange === 'all' && t('common.all')}
              </span>
            </div>
          </div>

          {/* Filter Bar */}
          <FilterBar
            onFilterChange={(updatedFilters) => setFilters(updatedFilters)}
            activeFilters={filters}
            totalCount={filteredRecognitions.length}
            departments={departmentOptions}
            companyValues={companyRecognitionValues}
          />

          {/* Recognition Feed */}
          {filteredRecognitions.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="flex flex-col gap-3 relative max-w-3xl mx-auto w-full mt-4">
              {filteredRecognitions.slice(0, displayCount).map((rec) => (
                <div key={rec.id} className="relative">
                  <RecognitionCard
                    recognition={{
                      id: rec.id,
                      senderId: rec.senderId || "",
                      senderName: rec.senderName,
                      senderAvatar: rec.senderAvatar,
                      receiverId: rec.receiverId || "",
                      receiverName: rec.receiverName,
                      receiverAvatar: rec.recipientAvatar,
                      message: rec.message,
                      values: rec.values,
                      timestamp: rec.timestamp,
                      type: rec.type
                    }}
                    currentUserId="current-user-id"
                  />
                </div>
              ))}

              {/* Load More */}
              {displayCount < filteredRecognitions.length && (
                <div className="text-center py-4 border-t border-border mt-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('performance.recognition.showingXofY', {
                      shown: displayCount,
                      total: filteredRecognitions.length
                    })}
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" size="lg" onClick={handleLoadMore}>
                      {t('performance.recognition.loadMore')} ({Math.min(ITEMS_PER_PAGE, filteredRecognitions.length - displayCount)})
                    </Button>
                    <Button variant="ghost" size="lg" onClick={handleShowAll}>
                      {t('performance.recognition.showAll')} ({filteredRecognitions.length - displayCount} {t('common.more')})
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          {filteredRecognitions.length > 0 && (
            <div className="mt-8 pt-3 border-t border-border text-center max-w-3xl mx-auto w-full">
              <p className="text-sm text-muted-foreground mb-2">
                {t('performance.recognition.autoRefreshNote')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
              >
                <AppIcon name="RotateCcw" size={16} className="me-2" />
                {isRefreshing ? t('performance.recognition.refreshing') : t('performance.recognition.refreshNow')}
              </Button>
            </div>
          )}
        </>
      )}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default RecognitionFeed;
